// frontend/src/hooks/usePlatformProjectMutations.ts — the platform-projects write
// surface (create · update · lifecycle). Each invalidates the `['platform-projects']`
// PREFIX on success, so the list + any open detail refetch together
// (FRONTEND-PATTERNS §Mutations + invalidation).
//
// Errors are NOT caught here — they bubble so the form shell / ConfirmDialog map the
// backend `code` through errors.json. 401/403 is the apiClient's job (ADR-003).

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type {
  CreateLicenseTierRequest,
  CreatePlatformProjectRequest,
  UpdateLicenseTierRequest,
  UpdatePlatformProjectRequest,
} from '@ems-app/schemas/projects/platform-requests';
import type { CreatedLicenseTierResponse, CreatedProjectResponse } from '@ems-app/schemas/projects/platform-responses';
import { apiClient } from '../lib/apiClient.js';
import { platformProjectsKeys } from './usePlatformProjects.js';
import { useInvalidateProjectViews } from './useProject.js';
import { refreshOnSettled } from '../lib/settled.js';

function useInvalidateProjects(): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: platformProjectsKeys.all });
}

/** POST /platform/projects — atomic create; returns the new project id. */
export function useCreatePlatformProject(): UseMutationResult<CreatedProjectResponse, Error, CreatePlatformProjectRequest> {
  const invalidate = useInvalidateProjects();
  const invalidateProjectViews = useInvalidateProjectViews();
  return useMutation({
    mutationFn: async (body) => (await apiClient.post<CreatedProjectResponse>('/platform/projects', body)).data,
    onSuccess: () => {
      invalidate();
      // No active `['project', id]` yet; the new row appears via the /me list.
      invalidateProjectViews();
    },
  });
}

/** PATCH /platform/projects/:id — partial update (identity · brand · countries ·
 *  modules · seats · trial). 204; invalidate refreshes list + detail + peek. */
export function useUpdatePlatformProject(): UseMutationResult<void, Error, { id: string; body: UpdatePlatformProjectRequest }> {
  const invalidate = useInvalidateProjects();
  const invalidateProjectViews = useInvalidateProjectViews();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      await apiClient.patch(`/platform/projects/${id}`, body);
    },
    onSuccess: (_data, { id }) => {
      invalidate();
      // The same row the switcher + active theme read — a sysadmin editing a
      // project they are also a member of must see the switcher + chrome update
      // without a refresh (identity/brand), and a module toggle must re-gate the
      // nav (entitlements ride `['me']`).
      invalidateProjectViews(id);
    },
  });
}

/** POST /platform/projects/:id/{suspend|reactivate|terminate} — lifecycle. 204. */
export function useProjectLifecycle(): UseMutationResult<void, Error, { id: string; action: 'suspend' | 'reactivate' | 'terminate' }> {
  const invalidate = useInvalidateProjects();
  const invalidateProjectViews = useInvalidateProjectViews();
  return useMutation({
    mutationFn: async ({ id, action }) => {
      await apiClient.post(`/platform/projects/${id}/${action}`);
    },
    onSettled: refreshOnSettled((_data, { id }) => {
      invalidate();
      // A status change flips the switcher's badge + the blocked-page routing,
      // both read from the `['me']` list.
      invalidateProjectViews(id);
    }),
  });
}

function useInvalidateTiers(): () => void {
  const qc = useQueryClient();
  // A tier add/rename touches the catalogue every project surface reads (wizard,
  // licensing drawer, tiers modal) — invalidate the tiers key.
  return () => void qc.invalidateQueries({ queryKey: platformProjectsKeys.tiers });
}

/** POST /platform/license-tiers — add a tier to the global catalogue. */
export function useCreateLicenseTier(): UseMutationResult<CreatedLicenseTierResponse, Error, CreateLicenseTierRequest> {
  const invalidate = useInvalidateTiers();
  return useMutation({
    mutationFn: async (body) => (await apiClient.post<CreatedLicenseTierResponse>('/platform/license-tiers', body)).data,
    onSuccess: invalidate,
  });
}

/** PATCH /platform/license-tiers/:code — rename a tier. 204. */
export function useUpdateLicenseTier(): UseMutationResult<void, Error, { code: string; body: UpdateLicenseTierRequest }> {
  const invalidate = useInvalidateTiers();
  return useMutation({
    mutationFn: async ({ code, body }) => {
      await apiClient.patch(`/platform/license-tiers/${code}`, body);
    },
    onSuccess: invalidate,
  });
}
